import { call, put, all, select, cancelled } from 'redux-saga/effects';

import UserClient from '../../api/UserClient';
import {
    billingListSuccessful,
    billingListFailed,
    billingSetResolvedProfile,
    billingFetchCancelled,
} from '../../redux/billing/actions';
import { BILLING_LIST_REQUEST, SCOPE_TRIPS, SCOPE_OTHERS } from '../../redux/billing/types';
import { logoutRequest } from '../../redux/login/actions';
import { messagesShow } from '../../redux/messages/actions';
import getUser from '../user/get';
import { meSelector, listProfileSelector } from '../../selectors/user';
import { profileSelector, dateFilterSelector, activeScopeSelector, rawFilterSelector } from '../../selectors/billing';
import { CONST_PROFILE_TYPE_SINGLE } from '../../helpers/user';

export default function*(action) {
    const isFullLoad = !action || action.type === BILLING_LIST_REQUEST;
    const scope = action && action.payload && action.payload.scope;

    // Fetching flags this task owns — locked from action scope so a mid-flight
    // tab switch cannot leave the wrong scope spinning forever.
    const pendingScopes =
        scope && !isFullLoad
            ? { trips: scope === SCOPE_TRIPS, others: scope === SCOPE_OTHERS }
            : { trips: true, others: true };

    const lockedFilter = scope ? yield select(rawFilterSelector) : null;
    const lockedDateFilter = scope ? yield select(dateFilterSelector) : null;

    try {
        yield call(getUser);
        const me = yield select(meSelector);
        let profileId = (lockedFilter && lockedFilter.profile) || (yield select(profileSelector));
        const profiles = yield select(listProfileSelector);
        const dateFilter = scope ? lockedDateFilter : yield select(dateFilterSelector);
        const rawFilter = lockedFilter || (yield select(rawFilterSelector));

        if (!me) {
            yield call(getUser);
        }

        if (!profileId && profiles && profiles.length > 0) {
            const personalProfile = profiles.find(p => p.type === CONST_PROFILE_TYPE_SINGLE) || profiles[0];
            profileId = personalProfile.id;
            yield put(billingSetResolvedProfile(profileId));
        }

        const selectedProfile = profiles.find(p => p.id === profileId);
        const selectedProfileType = selectedProfile ? selectedProfile.type : 'Single';

        const activeScope = scope || isFullLoad ? null : yield select(activeScopeSelector);
        const fetchTrips = isFullLoad || scope === SCOPE_TRIPS || activeScope === SCOPE_TRIPS;
        const fetchOthers = isFullLoad || scope === SCOPE_OTHERS || activeScope === SCOPE_OTHERS;

        const calls = [];
        const indexMap = {};
        if (fetchTrips) {
            calls.push(
                dateFilter
                    ? call(
                          UserClient.fetchMyTrips,
                          selectedProfileType,
                          profileId,
                          dateFilter.startDate,
                          dateFilter.endDate
                      )
                    : call(UserClient.fetchMyTrips, selectedProfileType, profileId)
            );
            indexMap.trips = calls.length - 1;
        }
        if (fetchOthers) {
            calls.push(
                dateFilter
                    ? call(UserClient.fetchMyProducts, dateFilter.startDate, dateFilter.endDate)
                    : call(UserClient.fetchMyProducts)
            );
            indexMap.others = calls.length - 1;
        }
        if (isFullLoad) {
            calls.push(call(UserClient.fetchMyOldInvoices));
            indexMap.old = calls.length - 1;
        }

        const results = yield all(calls);

        yield put(
            billingListSuccessful({
                trips: indexMap.trips !== undefined ? results[indexMap.trips] : undefined,
                others: indexMap.others !== undefined ? results[indexMap.others] : undefined,
                old: indexMap.old !== undefined ? results[indexMap.old] : undefined,
                isFiltered: !!dateFilter,
                filterUsed: { year: rawFilter.year, month: rawFilter.month, profile: profileId },
                scopesFetched: { trips: fetchTrips, others: fetchOthers },
            })
        );
    } catch (error) {
        if (error.code === 401) {
            yield put(logoutRequest());
        } else {
            yield put(billingListFailed());
            yield put(messagesShow('internalServerError', 'error'));
        }
    } finally {
        if (yield cancelled()) {
            yield put(billingFetchCancelled(pendingScopes));
        }
    }
}
